import {flow, get, isEmpty, negate} from 'lodash/fp';
import {createSelector} from 'reselect';

import {BUSINESS_COACH, BUSINESS_LEADER, COACH} from '/enum/competency.enum';
import {LABEL_COMING_SOON} from '/enum/label.enum';
import {fromProps} from '/util/selector.util';

import {metric as metricState} from './root.selector';

export const clientMetrics = createSelector(metricState, get('allClients'));

export const clientMetricsById = createSelector(
  fromProps(get('coachId')),
  clientMetrics,
  (coachId, metrics) => {
    return get(coachId)(metrics);
  },
);

export const hasClientMetricsById = createSelector(
  clientMetricsById,
  negate(isEmpty),
);

// ============ clients ==============

export const newClientsCountFrontlineById = createSelector(
  clientMetricsById,
  get('clients.newFrontlineClientsCount'),
);

export const newClientsCountGen0ById = createSelector(
  clientMetricsById,
  get('clients.newGen0ClientsCount'),
);

export const newClientsCountOrgById = createSelector(
  clientMetricsById,
  get('clients.newOrgClientsCount'),
);

export const clientsCountFrontlineById = createSelector(
  clientMetricsById,
  get('clients.frontlineClientsCount'),
);

export const clientsCountGen0ById = createSelector(
  clientMetricsById,
  get('clients.gen0ClientsCount'),
);

export const clientsCountOrgById = createSelector(
  clientMetricsById,
  get('clients.orgClientsCount'),
);

export const lastMonthClientsCountFrontlineById = createSelector(
  clientMetricsById,
  get('clients.lastMonthFrontlineClientsCount'),
);

export const lastMonthClientsCountGen0ById = createSelector(
  clientMetricsById,
  get('clients.lastMonthGen0ClientsCount'),
);

export const lastMonthClientsCountOrgById = createSelector(
  clientMetricsById,
  get('clients.lastMonthOrgClientsCount'),
);

export const deltaClientsCountFrontlineById = createSelector(
  clientsCountFrontlineById,
  lastMonthClientsCountFrontlineById,
  (count, countLastMonth) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    return ((count - countLastMonth) / countLastMonth) * 100;
  },
);

export const deltaClientsCountGen0ById = createSelector(
  clientsCountGen0ById,
  lastMonthClientsCountGen0ById,
  (count, countLastMonth) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    return ((count - countLastMonth) / countLastMonth) * 100;
  },
);

export const deltaClientsCountOrgById = createSelector(
  clientsCountOrgById,
  lastMonthClientsCountOrgById,
  (count, countLastMonth) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    return ((count - countLastMonth) / countLastMonth) * 100;
  },
);

export const totalGenReactivatedClientsCountById = createSelector(
  clientMetricsById,
  get('clients.tGenReactivatedClientsCount'),
);

export const frontlineReactivatedClientsCountById = createSelector(
  clientMetricsById,
  get('clients.frontlineReactivatedClientsCount'),
);

export const clientSummaryMetricsById = createSelector(
  newClientsCountFrontlineById,
  newClientsCountGen0ById,
  newClientsCountOrgById,
  clientsCountFrontlineById,
  clientsCountGen0ById,
  clientsCountOrgById,
  lastMonthClientsCountFrontlineById,
  lastMonthClientsCountGen0ById,
  lastMonthClientsCountOrgById,
  deltaClientsCountFrontlineById,
  deltaClientsCountGen0ById,
  deltaClientsCountOrgById,
  totalGenReactivatedClientsCountById,
  frontlineReactivatedClientsCountById,
  (
    newClientsCountFrontline,
    newClientsCountGen0,
    newClientsCountOrg,
    clientsCountFrontline,
    clientsCountGen0,
    clientsCountOrg,
    lastMonthClientsCountFrontline,
    lastMonthClientsCountGen0,
    lastMonthClientsCountOrg,
    deltaClientsCountFrontline,
    deltaClientsCountGen0,
    deltaClientsCountOrg,
    tGenReactivatedCount,
    frontlineReactivatedCount,
  ) => {
    return {
      [COACH]: {
        newCount: newClientsCountFrontline,
        totalCount: clientsCountFrontline,
        lastMonthCount: lastMonthClientsCountFrontline,
        delta: deltaClientsCountFrontline,
        tGenReactivatedCount: tGenReactivatedCount,
        frontlineReactivatedCount: frontlineReactivatedCount,
      },
      [BUSINESS_COACH]: {
        newCount: newClientsCountGen0,
        totalCount: clientsCountGen0,
        lastMonthCount: lastMonthClientsCountGen0,
        delta: deltaClientsCountGen0,
        tGenReactivatedCount: tGenReactivatedCount,
        frontlineReactivatedCount: frontlineReactivatedCount,
      },
      [BUSINESS_LEADER]: {
        newCount: newClientsCountOrg,
        totalCount: clientsCountOrg,
        lastMonthCount: lastMonthClientsCountOrg,
        delta: deltaClientsCountOrg,
        tGenReactivatedCount: tGenReactivatedCount,
        frontlineReactivatedCount: frontlineReactivatedCount,
      },
    };
  },
);

export const clientSummaryMetricsByIdByCompetency = createSelector(
  fromProps(get('competency')),
  clientSummaryMetricsById,
  (competency, clientSummaryMetrics) => {
    return get(competency)(clientSummaryMetrics);
  },
);

export const premierCancellationsCountById = createSelector(
  clientMetricsById,
  get('clients.premierCancellationsCount'),
);

export const newClientsWithoutOrdersCountById = createSelector(
  clientMetricsById,
  get('clients.newClientsWithoutOrdersCount'),
);

export const inactiveClientsCountById = createSelector(
  clientMetricsById,
  get('clients.inactiveClientsCount'),
);

export const expiringCreditsCountFrontlineById = createSelector(
  clientMetricsById,
  get('clients.frontlineClientsWithExpiringWellnessCreditsCount'),
);

export const expiringRewardsCountFrontlineById = createSelector(
  clientMetricsById,
  get('clients.frontlineClientsWithExpiringRewardsCount'),
);

export const expiringRewardsCountAllOrgById = createSelector(
  clientMetricsById,
  get('clients.allOrgClientsWithExpiringRewardsCount'),
);
// ============ orders ==============

export const newOrdersCountFrontlineById = createSelector(
  clientMetricsById,
  get('orders.frontlineOrdersFromNewClientsCount'),
);

export const newOrdersCountGen0ById = createSelector(
  clientMetricsById,
  get('orders.gen0OrdersFromNewClientsCount'),
);

export const newOrdersCountOrgById = createSelector(
  clientMetricsById,
  get('orders.orgOrdersFromNewClientsCount'),
);

export const ordersCountFrontlineById = createSelector(
  clientMetricsById,
  get('orders.frontlineOrdersCount'),
);
export const ordersCountGen0ById = createSelector(
  clientMetricsById,
  get('orders.gen0OrdersCount'),
);

export const ordersCountOrgById = createSelector(
  clientMetricsById,
  get('orders.orgOrdersCount'),
);

export const allOrdersCountOrgById = createSelector(
  clientMetricsById,
  get('orders.orgOrdersCount'),
);

// ============ candidates ==============
export const allCandidatesCountById = createSelector(
  clientMetricsById,
  get('candidates.allCandidatesCount'),
);

export const referralsCountById = createSelector(
  clientMetricsById,
  get('candidates.referralsCount'),
);

// ============ volume ==============

export const currentVolumeFrontlineById = createSelector(
  clientMetricsById,
  get('orders.frontlineVolume.current'),
);

export const projectedVolumeFrontlineById = createSelector(
  clientMetricsById,
  get('orders.frontlineVolume.projected'),
);

export const currentVolumeGen0ById = createSelector(
  clientMetricsById,
  get('orders.gen0Volume.current'),
);

export const projectedVolumeGen0ById = createSelector(
  clientMetricsById,
  get('orders.gen0Volume.projected'),
);

export const currentVolumeOrgById = createSelector(
  clientMetricsById,
  flow([get('orders.totalGenerationVolume.current'), parseInt]),
);

export const projectedVolumeOrgById = createSelector(
  clientMetricsById,
  flow([get('orders.totalGenerationVolume.projected'), parseInt]),
);

export const estimatedVolumeFrontlineById = createSelector(
  clientMetricsById,
  ({orders}) => {
    const estimatedVolume =
      parseInt(get('frontlineVolume.current')(orders), 10) +
      parseInt(get('frontlineVolume.projected')(orders), 10);
    return get('frontlineVolume.projected')(orders) === LABEL_COMING_SOON
      ? LABEL_COMING_SOON
      : // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
        parseInt(estimatedVolume, 10);
  },
);

export const estimatedVolumeGen0ById = createSelector(
  clientMetricsById,
  ({orders}) => {
    const estimatedVolume =
      parseInt(get('gen0Volume.current')(orders), 10) +
      parseInt(get('gen0Volume.projected')(orders), 10);
    return get('gen0Volume.projected')(orders) === LABEL_COMING_SOON
      ? LABEL_COMING_SOON
      : // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
        parseInt(estimatedVolume, 10);
  },
);

export const estimatedVolumeOrgById = createSelector(
  clientMetricsById,
  ({orders}) => {
    const estimatedVolume =
      parseInt(get('totalGenerationVolume.current')(orders), 10) +
      parseInt(get('totalGenerationVolume.projected')(orders), 10);
    return get('totalGenerationVolume.projected')(orders) === LABEL_COMING_SOON
      ? LABEL_COMING_SOON
      : // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
        parseInt(estimatedVolume, 10);
  },
);

export const volumeSummaryMetricsById = createSelector(
  currentVolumeFrontlineById,
  currentVolumeGen0ById,
  currentVolumeOrgById,
  projectedVolumeFrontlineById,
  projectedVolumeGen0ById,
  projectedVolumeOrgById,
  estimatedVolumeFrontlineById,
  estimatedVolumeGen0ById,
  estimatedVolumeOrgById,
  (
    currentVolumeFrontline,
    currentVolumeGen0,
    currentVolumeOrg,
    projectedVolumeFrontline,
    projectedVolumeGen0,
    projectedVolumeOrg,
    estimatedVolumeFrontline,
    estimatedVolumeGen0,
    estimatedVolumeOrg,
  ) => {
    return {
      [COACH]: {
        currentVolume: currentVolumeFrontline,
        projectedVolume: projectedVolumeFrontline,
        estimatedVolume: estimatedVolumeFrontline,
      },
      [BUSINESS_COACH]: {
        currentVolume: currentVolumeGen0,
        projectedVolume: projectedVolumeGen0,
        estimatedVolume: estimatedVolumeGen0,
      },
      [BUSINESS_LEADER]: {
        currentVolume: currentVolumeOrg,
        projectedVolume: projectedVolumeOrg,
        estimatedVolume: estimatedVolumeOrg,
      },
    };
  },
);

export const volumeSummaryMetricsByIdByCompetency = createSelector(
  fromProps(get('competency')),
  volumeSummaryMetricsById,
  (competency, volumeMetrics) => {
    return get(competency)(volumeMetrics);
  },
);

export const projectedPremierMetricsById = createSelector(
  currentVolumeFrontlineById,
  currentVolumeGen0ById,
  currentVolumeOrgById,
  projectedVolumeFrontlineById,
  projectedVolumeGen0ById,
  projectedVolumeOrgById,
  estimatedVolumeFrontlineById,
  estimatedVolumeGen0ById,
  estimatedVolumeOrgById,
  (
    currentVolumeFrontline,
    currentVolumeGen0,
    currentVolumeOrg,
    projectedVolumeFrontline,
    projectedVolumeGen0,
    projectedVolumeOrg,
    estimatedVolumeFrontline,
    estimatedVolumeGen0,
    estimatedVolumeOrg,
  ) => {
    return {
      [COACH]: {
        currentVolume: currentVolumeOrg,
        projectedVolume: projectedVolumeOrg,
        estimatedVolume: estimatedVolumeOrg,
      },
      [BUSINESS_COACH]: {
        currentVolume: currentVolumeOrg,
        projectedVolume: projectedVolumeOrg,
        estimatedVolume: estimatedVolumeOrg,
      },
      [BUSINESS_LEADER]: {
        currentVolume: currentVolumeOrg,
        projectedVolume: projectedVolumeOrg,
        estimatedVolume: estimatedVolumeOrg,
      },
    };
  },
);

export const projectedPremierMetricsByIdByCompetency = createSelector(
  fromProps(get('competency')),
  projectedPremierMetricsById,
  (competency, volumeMetrics) => {
    return get(competency)(volumeMetrics);
  },
);
